import {
  Check,
  X,
  Pencil,
  Eye,
  Download,
  FileText,
  Send,
  MessageCircle,
  Undo2,
  Loader2,
  Settings,
  ClipboardCheck,
} from "lucide-react";
import { StatusBadge } from "./StatusBadge";
import { useTheme } from "@/context/ThemeContext";
import { VehicleTest } from "@/types/vehicleTest";
import { LicensePlate } from "@/components/my-auctions/LicensePlate";
import TestProtocol from "@/components/car-listing/TestProtocol";
import { VerifyVehicleInfoForm } from "./VerifyVehicleInfoForm";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import {
  ColoredOutlineButton,
  OUTLINE_TEXT_BUTTON_ICON_CLASS,
} from "@/components/ui/colored-outline-button";
import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useMedia } from "@/hooks/useMedia";
import { carTestLabels } from "@/i18n/labels";
import { useLanguage } from "@/context/LanguageContext";

import { useNativePlatform } from "@/hooks/useNativePlatform";
import {
  NativeOptionSheet,
  type NativeOption,
} from "@/components/native/NativeOptionSheet";

type ManageAction = {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
};

function TestManageMenu({
  actions,
  label,
  buttonClassName,
  iconClassName,
  triggerClassName,
}: {
  actions: ManageAction[];
  label: string;
  buttonClassName?: string;
  iconClassName?: string;
  triggerClassName?: string;
}) {
  const { isNative } = useNativePlatform();
  const [open, setOpen] = useState(false);

  if (actions.length === 0) return null;

  const triggerButton = (
    <ColoredOutlineButton
      variant="neutral"
      tintedBackground
      size="sm"
      className={cn(
        "h-6 sm:h-7 px-2 sm:px-2.5 text-[11px] sm:text-xs gap-0.5 sm:gap-1 py-0 native-touch-btn",
        buttonClassName,
      )}
      data-dropdown-trigger
      onClick={(e) => {
        e.stopPropagation();
        if (isNative) setOpen(true);
      }}
    >
      <Settings
        className={cn(
          OUTLINE_TEXT_BUTTON_ICON_CLASS,
          "h-3 w-3 sm:h-3.5 sm:w-3.5",
          iconClassName,
        )}
        strokeWidth={2.35}
      />
      <span>{label}</span>
    </ColoredOutlineButton>
  );

  if (isNative) {
    const options: NativeOption[] = actions.map((action, index) => ({
      value: String(index),
      label: action.label,
      icon: action.icon,
      disabled: action.disabled,
    }));

    return (
      <>
        <div className={triggerClassName}>{triggerButton}</div>
        <NativeOptionSheet
          open={open}
          onOpenChange={setOpen}
          title={label}
          value=""
          options={options}
          onSelect={(value) => {
            const action = actions[Number(value)];
            if (!action || action.disabled) return;
            setOpen(false);
            window.setTimeout(() => action.onClick(), 50);
          }}
        />
      </>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className={triggerClassName} asChild>
        {triggerButton}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        {actions.map((action, index) => (
          <DropdownMenuItem
            key={index}
            onClick={(e) => {
              e.stopPropagation();
              action.onClick();
            }}
            disabled={action.disabled}
            className={action.className || ""}
          >
            <span className="mr-2">{action.icon}</span>
            {action.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function formatTestDate(d: string | Date): string {
  const date = typeof d === "string" ? new Date(d) : d;
  if (isNaN(date.getTime())) return typeof d === "string" ? d : "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const h = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day} ${h}:${min}`;
}

function TestTimestamps({
  test,
  startedLabel,
  updatedLabel,
  className,
}: {
  test: VehicleTest;
  startedLabel: string;
  updatedLabel: string;
  className?: string;
}) {
  const created = test.createdAt || test.date;
  const updated = test.updatedAt;
  return (
    <div
      className={cn(
        "flex flex-col gap-0 text-zinc-500 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-3 sm:gap-y-0",
        className,
      )}
    >
      {created ? (
        <span className="whitespace-nowrap tabular-nums leading-tight">
          {startedLabel}: {formatTestDate(created)}
        </span>
      ) : null}
      {updated ? (
        <span className="whitespace-nowrap tabular-nums leading-tight">
          {updatedLabel}: {formatTestDate(updated)}
        </span>
      ) : null}
    </div>
  );
}

interface ActiveTestProps {
  test: VehicleTest;
  onEdit?: (test: VehicleTest) => void;
  onApprove?: () => void;
  onReject?: () => void;
  isAnimating?: boolean;
  isUpdating?: boolean;
  onView?: (test: VehicleTest) => void;
  onDownload?: (test: VehicleTest) => void;
  onCreateAd?: (test: VehicleTest) => void;
  onRequestComplement?: (test: VehicleTest) => void;
  onComplete?: (test: VehicleTest) => void;
  onRequestChange?: (test: VehicleTest) => void;
  /** Withdraw PENDING_REVIEW → ONGOING so user can edit again */
  onWithdrawReview?: (test: VehicleTest) => void;
  onConfirmChangeRequest?: (test: VehicleTest) => void;
  isAdmin?: boolean;
  /** When false, hide mutating admin actions (approve/verify/etc). Default true for admin callers that already gated callbacks. */
  canManageVehicles?: boolean;
  /** When true, card click only selects (e.g. archive/unarchive mode); does not open view popup */
  selectionMode?: boolean;
}

export const ActiveTest = ({
  test,
  onEdit,
  onApprove,
  onReject,
  isAnimating,
  isUpdating,
  onView,
  onDownload,
  onCreateAd,
  onRequestComplement,
  onComplete,
  onRequestChange,
  onWithdrawReview,
  onConfirmChangeRequest,
  isAdmin = false,
  canManageVehicles = true,
  selectionMode = false,
}: ActiveTestProps) => {
  const { theme } = useTheme();
  const { language } = useLanguage();
  const ct = carTestLabels(language);
  const isDark = theme === "dark";
  const [isTestProtocolOpen, setIsTestProtocolOpen] = useState(false);
  const [verifyVehicleInfoTest, setVerifyVehicleInfoTest] =
    useState<VehicleTest | null>(null);
  const [showApproveConfirmDialog, setShowApproveConfirmDialog] =
    useState(false);
  const [complementDialogTest, setComplementDialogTest] =
    useState<VehicleTest | null>(null);
  const [changeRequestTooltipOpen, setChangeRequestTooltipOpen] =
    useState(false);
  const [changeRequestTooltipOpenDesktop, setChangeRequestTooltipOpenDesktop] =
    useState(false);
  const { getDownloadUrlAsync } = useMedia();
  const [previewUrls, setPreviewUrls] = useState<Map<string, string>>(
    new Map(),
  );

  // Use test directly since it's already a VehicleTest
  const vehicleTest = test;

  // Fetch preview URLs for thumbnail images on mount
  useEffect(() => {
    const fetchPreviewUrl = async () => {
      const exteriorImages = test.images?.exterior || [];
      const thumbnailImage =
        exteriorImages.find((img) => img.isThumbnail) || exteriorImages[0];

      if (
        thumbnailImage?.previewMediaId &&
        thumbnailImage.previewMediaId !== thumbnailImage.mediaId
      ) {
        try {
          const previewUrl = await getDownloadUrlAsync({
            mediaId: thumbnailImage.previewMediaId,
            expiresIn: 3600,
          });
          setPreviewUrls(
            new Map([[thumbnailImage.previewMediaId, previewUrl]]),
          );
        } catch (error) {}
      }
    };

    fetchPreviewUrl();
  }, [test.images?.exterior, getDownloadUrlAsync]);

  // Handle card click - open dialog (unless in selection mode e.g. archive/unarchive)
  const handleCardClick = (e: React.MouseEvent) => {
    if (selectionMode) return;
    // Don't open dialog if clicking on dropdown or its children
    if (
      (e.target as HTMLElement).closest("[data-dropdown-trigger]") ||
      (e.target as HTMLElement).closest("[role='menu']")
    ) {
      return;
    }
    if (onView) {
      onView(test);
    }
  };

  // Build available actions based on test status and user role
  const getAvailableActions = () => {
    const actions: Array<{
      label: string;
      icon: React.ReactNode;
      onClick: () => void;
      disabled?: boolean;
      className?: string;
    }> = [];

    // View test protocol (always available if onView exists) - this is the main view
    // The card click also opens this, but it's still useful to have in dropdown for clarity

    // View detailed test protocol (opens TestProtocol dialog)
    actions.push({
      label: ct.actions.viewTestProtocol,
      icon: <Eye className="h-4 w-4" />,
      onClick: () => setIsTestProtocolOpen(true),
    });

    // Download PDF — always when handler is provided (incl. active-listing / complement / ongoing)
    if (onDownload) {
      actions.push({
        label: ct.actions.download,
        icon: <Download className="h-4 w-4" />,
        onClick: () => onDownload(test),
      });
    }

    // Edit (complement for users opens via Komplettera dialog, not Hantera)
    if (
      onEdit &&
      !test.isArchived &&
      test.status !== "completed" &&
      (isAdmin ||
        test.status === "ongoing" ||
        (test.status === "complement" && isAdmin))
    ) {
      actions.push({
        label: ct.actions.edit,
        icon: <Pencil className="h-4 w-4" />,
        onClick: () => onEdit(test),
        disabled: isAnimating || isUpdating,
      });
    }

    // Verifiera fordonsinfo – kräver Hantera fordonstester
    if (
      isAdmin &&
      canManageVehicles &&
      ["pending-review", "complement", "approved", "active-listing"].includes(
        test.status,
      )
    ) {
      actions.push({
        label: ct.actions.verifyVehicleInfo,
        icon: <ClipboardCheck className="h-4 w-4" />,
        onClick: () => setVerifyVehicleInfoTest(test),
      });
    }

    // Admin action for change-requested: confirm change done
    if (
      isAdmin &&
      test.status === "change-requested" &&
      onConfirmChangeRequest
    ) {
      actions.push({
        label:
          (ct.actions as Record<string, string>)?.confirmChangeDone ??
          "Bekräfta åtgärd",
        icon: isUpdating ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Check className="h-4 w-4" />
        ),
        onClick: () => onConfirmChangeRequest(test),
        disabled: isAnimating || isUpdating,
        className: "text-green-600",
      });
    }

    // Admin actions for pending-review or complement
    if (
      isAdmin &&
      (test.status === "pending-review" || test.status === "complement")
    ) {
      if (onApprove) {
        actions.push({
          label: ct.actions.approve,
          icon: isUpdating ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Check className="h-4 w-4" />
          ),
          onClick: () => setShowApproveConfirmDialog(true),
          disabled: isAnimating || isUpdating,
          className: "text-green-600",
        });
      }
      if (onReject) {
        actions.push({
          label: ct.actions.reject,
          icon: isUpdating ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <X className="h-4 w-4" />
          ),
          onClick: onReject,
          disabled: isAnimating || isUpdating,
          className: "text-red-600",
        });
      }
      if (test.status === "pending-review" && onRequestComplement) {
        actions.push({
          label: ct.actions.requestComplement,
          icon: <Send className="h-4 w-4" />,
          onClick: () => onRequestComplement(test),
          disabled: isAnimating || isUpdating,
          className: "text-orange-600",
        });
      }
    }

    // PENDING_REVIEW: withdraw back to ongoing (self-edit), not request-change
    if (!isAdmin && test.status === "pending-review" && onWithdrawReview) {
      actions.push({
        label: ct.actions.withdrawReview,
        icon: <Undo2 className="h-4 w-4" />,
        onClick: () => onWithdrawReview(test),
        disabled: isAnimating || isUpdating,
      });
    }

    // Approved / active listing: request change for admin to handle
    if (
      !isAdmin &&
      (test.status === "approved" || test.status === "active-listing") &&
      onRequestChange
    ) {
      actions.push({
        label: ct.actions.requestChange,
        icon: <MessageCircle className="h-4 w-4" />,
        onClick: () => onRequestChange(test),
        disabled: isAnimating || isUpdating,
      });
    }

    // Complete test (non-admin, ongoing status)
    if (!isAdmin && test.status === "ongoing" && onComplete) {
      actions.push({
        label: ct.actions.complete,
        icon: <Check className="h-4 w-4" />,
        onClick: () => onComplete(test),
        disabled: isAnimating || isUpdating,
      });
    }

    // Admin actions for approved tests
    if (isAdmin && test.status === "approved" && onCreateAd) {
      actions.push({
        label: ct.actions.createAd,
        icon: <FileText className="h-4 w-4" />,
        onClick: () => onCreateAd(test),
      });
    }

    // Admin actions for rejected tests
    if (isAdmin && test.status === "rejected" && onRequestComplement) {
      actions.push({
        label: ct.actions.requestComplement,
        icon: <Send className="h-4 w-4" />,
        onClick: () => onRequestComplement(test),
      });
    }

    return actions;
  };

  const availableActions = getAvailableActions();

  // Get thumbnail image (preview if available, otherwise use url)
  const getThumbnailUrl = () => {
    const exteriorImages = test.images?.exterior || [];
    const thumbnailImage =
      exteriorImages.find((img) => img.isThumbnail) || exteriorImages[0];
    if (!thumbnailImage) return null;

    // Use preview URL if available (PREVIEW variant for better performance)
    if (
      thumbnailImage.previewMediaId &&
      thumbnailImage.previewMediaId !== thumbnailImage.mediaId
    ) {
      const previewUrl = previewUrls.get(thumbnailImage.previewMediaId);
      if (previewUrl) {
        return previewUrl;
      }
      // If preview URL not yet loaded, return null to show nothing until it loads
      return null;
    }
    // Fallback to regular url if no preview available
    return thumbnailImage.url;
  };

  const thumbnailUrl = getThumbnailUrl();

  // Säljare räknas som ID-verifierad om KYC eller BankID är godkänd.
  const isSellerIdVerified =
    !!test.customer &&
    (test.customer.bankIdVerified === true ||
      test.customer.bankIdStatus?.toLowerCase() === "approved" ||
      test.customer.kycStatus?.toLowerCase() === "approved");

  return (
    <>
      <div
        className="bg-card backdrop-blur border rounded-lg overflow-hidden mb-2 shadow-sm transition-all cursor-pointer hover:shadow-md hover:border-primary/50 flex h-[80.5px] sm:h-[76px] min-w-0 w-full max-w-full"
        onClick={handleCardClick}
      >
        {/* Thumbnail - fixed area, image fits inside so vertical images don't change card height */}
        {thumbnailUrl && (
          <div className="flex-shrink-0 w-20 sm:w-24 h-full rounded-l-lg overflow-hidden">
            <img
              src={thumbnailUrl}
              alt={vehicleTest.vehicleName}
              className="w-full h-full object-cover"
              style={{ objectPosition: "center" }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          </div>
        )}
        <div className="flex flex-1 min-w-0 gap-2 sm:gap-3 items-center p-1.5 sm:p-2.5 overflow-hidden">
          <div className="flex-1 min-w-0">
            {/* Mobile Layout:
                Row 1: [Märke+modell] [status]
                Row 2: [licenseplate] [Hantera btn]
                Row 3: [Time]
            */}
            {/* Desktop: VehicleName | Status + Dropdown */}

            {/* Mobile: Row 1 - Title + Status (titellängd begränsad, overflow dolt) */}
            <div className="sm:hidden flex items-start justify-between gap-1.5 mb-0.5 min-w-0 overflow-hidden">
              <h3 className="text-[13px] font-semibold truncate flex-1 min-w-0 pt-0.5 leading-snug">
                {vehicleTest.vehicleName}
              </h3>
              <div className="flex items-start gap-1 min-w-0 flex-shrink overflow-hidden justify-end">
                {isAdmin && test.customer && !isSellerIdVerified && (
                  <span className="bg-amber-800/70 text-amber-100 text-[10px] px-1.5 py-0.5 rounded">
                    {(ct.actions as Record<string, string>).notIdVerified ??
                      "Ej ID-verifierad"}
                  </span>
                )}
                <div className="flex items-center gap-1">
                  <StatusBadge
                    status={vehicleTest.status}
                    className="!text-[10px]"
                  />
                  {isAdmin && test.status === "change-requested" && (
                    <TooltipProvider delayDuration={0}>
                      <Tooltip
                        open={changeRequestTooltipOpen}
                        onOpenChange={setChangeRequestTooltipOpen}
                      >
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex cursor-help text-amber-600 hover:text-amber-700 p-0.5 rounded touch-manipulation"
                            aria-label={
                              (ct.actions as Record<string, string>)
                                ?.changeRequestMessageTooltip
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              setChangeRequestTooltipOpen((o) => !o);
                            }}
                          >
                            <MessageCircle className="h-3.5 w-3.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs">
                          <p className="font-medium text-xs mb-1">
                            {(ct.actions as Record<string, string>)
                              ?.changeRequestMessageTooltip ??
                              "Användarens begäran"}
                          </p>
                          <p className="text-sm whitespace-pre-wrap">
                            {test.changeRequestMessage?.trim() ||
                              ((ct.actions as Record<string, string>)
                                ?.changeRequestNoMessage ??
                                "Ingen begäran angiven")}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
                {test.isArchived && (
                  <span className="bg-gray-500 text-white text-[10px] px-1.5 py-0.5 rounded">
                    {ct.actions.archived}
                  </span>
                )}
              </div>
            </div>

            {/* Mobile: Row 2 - LicensePlate + Komplettera (if complement) + Hantera */}
            <div className="sm:hidden flex items-center justify-between gap-1.5 min-h-6">
              <LicensePlate lpn={test.vehicleDetails.regNumber} />
              <div className="flex items-center gap-1 shrink-0">
                {!isAdmin && test.status === "complement" && onEdit && (
                  <Button
                    variant="default"
                    size="sm"
                    className="h-5 px-1.5 text-[11px] py-0 bg-orange-600 hover:bg-orange-700"
                    data-dropdown-trigger
                    onClick={(e) => {
                      e.stopPropagation();
                      setComplementDialogTest(test);
                    }}
                  >
                    <Send className="h-2.5 w-2.5 mr-0.5" />
                    {(ct.actions as Record<string, string>)?.sendComplement ??
                      "Komplettera"}
                  </Button>
                )}
                <TestManageMenu
                  actions={availableActions}
                  label={ct.actions.manage ?? "Hantera"}
                  triggerClassName="mt-0"
                />
              </div>
            </div>

            {/* Mobile: Row 3 - Timestamps */}
            <div className="sm:hidden mt-0.5">
              <TestTimestamps
                test={vehicleTest}
                startedLabel={ct.actions.started}
                updatedLabel={
                  (ct.actions as Record<string, string>).updated ?? "Uppdaterat"
                }
                className="text-[9px]"
              />
            </div>

            {/* Desktop: Row 1 - VehicleName | Status + Dropdown (overflow dolt så kort inte spiller) */}
            <div className="hidden sm:flex sm:justify-between sm:items-center sm:mb-1 gap-3 min-w-0 overflow-hidden">
              <h3 className="text-sm font-semibold truncate flex-1 min-w-0">
                {vehicleTest.vehicleName}
              </h3>
              <div className="flex items-center gap-2 min-w-0 flex-shrink-0">
                {isAdmin && test.customer && !isSellerIdVerified && (
                  <span className="bg-amber-800/70 text-amber-100 text-xs px-2 py-1 rounded">
                    {ct.actions.sellerNotIdVerified}
                  </span>
                )}
                <div className="flex items-center gap-1.5">
                  <StatusBadge status={vehicleTest.status} />
                  {isAdmin && test.status === "change-requested" && (
                    <TooltipProvider delayDuration={0}>
                      <Tooltip
                        open={changeRequestTooltipOpenDesktop}
                        onOpenChange={setChangeRequestTooltipOpenDesktop}
                      >
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex cursor-help text-amber-600 hover:text-amber-700 p-1 rounded touch-manipulation"
                            aria-label={
                              (ct.actions as Record<string, string>)
                                ?.changeRequestMessageTooltip
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              setChangeRequestTooltipOpenDesktop((o) => !o);
                            }}
                          >
                            <MessageCircle className="h-4 w-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs">
                          <p className="font-medium text-xs mb-1">
                            {(ct.actions as Record<string, string>)
                              ?.changeRequestMessageTooltip ??
                              "Användarens begäran"}
                          </p>
                          <p className="text-sm whitespace-pre-wrap">
                            {test.changeRequestMessage?.trim() ||
                              ((ct.actions as Record<string, string>)
                                ?.changeRequestNoMessage ??
                                "Ingen begäran angiven")}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
                {test.isArchived && (
                  <span className="bg-gray-500 text-white text-xs px-2 py-1 rounded">
                    {ct.actions.archived}
                  </span>
                )}
                {!isAdmin && test.status === "complement" && onEdit && (
                  <Button
                    variant="default"
                    size="sm"
                    className="h-8 px-3 text-xs bg-orange-600 hover:bg-orange-700"
                    data-dropdown-trigger
                    onClick={(e) => {
                      e.stopPropagation();
                      setComplementDialogTest(test);
                    }}
                  >
                    <Send className="h-3.5 w-3.5 mr-1.5" />
                    {(ct.actions as Record<string, string>)?.sendComplement ??
                      "Komplettera"}
                  </Button>
                )}
                <TestManageMenu
                  actions={availableActions}
                  label={ct.actions.manage}
                  buttonClassName="h-8 px-3"
                />
              </div>
            </div>

            {/* Desktop: Row 2 - LicensePlate + Date */}
            <div className="hidden sm:flex sm:items-center sm:gap-4 sm:flex-wrap">
              <LicensePlate lpn={test.vehicleDetails.regNumber} />
              <TestTimestamps
                test={vehicleTest}
                startedLabel={ct.actions.started}
                updatedLabel={
                  (ct.actions as Record<string, string>).updated ?? "Uppdaterat"
                }
                className="text-xs"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Test Protocol Dialog - for viewing test protocol details */}
      <Dialog open={isTestProtocolOpen} onOpenChange={setIsTestProtocolOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto overflow-x-hidden p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>
              {ct.dialogs.testProtocol.title.replace(
                "{vehicleName}",
                vehicleTest.vehicleName,
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-x-hidden">
            <TestProtocol
              vehicleTest={test}
              isCollapsible={false}
              showComments={true}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Verifiera fordonsinfo – redigerbar Fordonsinformation + Ytterligare information + Utrustning */}
      <Dialog
        open={!!verifyVehicleInfoTest}
        onOpenChange={(open) => !open && setVerifyVehicleInfoTest(null)}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto overflow-x-hidden p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="pb-4">
              {ct.dialogs.verifyVehicleInfo.title}
              {verifyVehicleInfoTest &&
                ` – ${verifyVehicleInfoTest.vehicleName}`}
            </DialogTitle>
          </DialogHeader>
          {verifyVehicleInfoTest && (
            <VerifyVehicleInfoForm
              test={verifyVehicleInfoTest}
              onClose={() => setVerifyVehicleInfoTest(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Kompletteringsbegäran – användarperspektiv */}
      <Dialog
        open={!!complementDialogTest}
        onOpenChange={(open) => {
          if (!open) setComplementDialogTest(null);
        }}
      >
        <DialogContent
          className="max-w-md"
          onClick={(e) => e.stopPropagation()}
        >
          <DialogHeader>
            <DialogTitle>
              {ct.dialogs.viewComplement.title}
            </DialogTitle>
            <DialogDescription>
              {ct.dialogs.viewComplement.description}
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border bg-muted/40 p-3 text-sm whitespace-pre-wrap">
            {complementDialogTest?.reviewNotes?.trim() ||
              ct.dialogs.viewComplement.noMessage}
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setComplementDialogTest(null)}
            >
              {ct.dialogs.viewComplement.close}
            </Button>
            <Button
              className="bg-orange-600 hover:bg-orange-700"
              onClick={() => {
                const t = complementDialogTest;
                setComplementDialogTest(null);
                if (t && onEdit) onEdit(t);
              }}
            >
              <Send className="mr-2 h-4 w-4" />
              {ct.dialogs.viewComplement.startComplement}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bekräftelsedialog för godkännande */}
      <Dialog
        open={showApproveConfirmDialog}
        onOpenChange={setShowApproveConfirmDialog}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="h-5 w-5 text-green-600" />
              {ct.dialogs.approve.title}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground font-bold">
              {ct.dialogs.approve.verifyQuestion}
            </p>
            <p className="text-sm">{ct.dialogs.approve.message}</p>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowApproveConfirmDialog(false)}
              disabled={isUpdating}
            >
              {ct.dialogs.approve.cancel}
            </Button>
            <Button
              onClick={() => {
                setShowApproveConfirmDialog(false);
                if (onApprove) {
                  onApprove();
                }
              }}
              disabled={isUpdating}
              className="bg-green-600 hover:bg-green-700"
            >
              {isUpdating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {ct.dialogs.approve.approving}
                </>
              ) : (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  {ct.dialogs.approve.approve}
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
